/* eslint-disable no-param-reassign */
import { ReactElement, useCallback } from "react";
import { editorAdapter } from "./editorsSlice";
import { useActiveEditor, useAllEditors, useAppSelector } from "./store/hooks";
import { allConnected } from "./allConnected";
import { Attachment } from "./injection/Attachment";
import { useInjection } from "./injection/useInjection";
import EditingDispatcher from "./dispatch/EditingDispatcher";
import { HoverLogic } from "./contents/underlines/hooks/useUnderlineCardHover";
import UnderlineShadowContainer from "./contents/underlines/UnderlineShadowContainer";
import DotShadowContainer from "./contents/dot/DotShadowContainer";

interface Props {
  attachment: Attachment;
  id: number;
  hoverLogic: HoverLogic;
}

function ContentEditableRoot({
  id,
  attachment,
  hoverLogic,
}: Props): ReactElement {
  const editorElement = attachment.editor;
  const editor = useAppSelector((state) =>
    editorAdapter.getSelectors().selectById(state.editors, id)
  );

  const injectUnderline = useCallback(
    () =>
      attachment.injectUnderlineRoot((div) => {
        div.style.position = "absolute";
        div.style.top = "0";
        div.style.left = "0";
      }),
    [attachment]
  );
  const underlineDomNode = useInjection(injectUnderline);

  const injectDot = useCallback(() => attachment.injectDotRoot(), [attachment]);
  const dotDomNode = useInjection(injectDot);

  // if the element doesn't exist or isn't connected to the DOM
  // Refactor warning: affects Type Safety below
  if (!editor || !allConnected(editorElement, dotDomNode)) {
    return <></>;
  }

  return (
    <>
      <EditingDispatcher
        editorElement={editorElement}
        editorId={id}
        shouldRefresh={true}
      />
      {/* Type safety: allConnected ensures all those elements are connected and defined */}
      <UnderlineShadowContainer
        editorId={id}
        underlineElement={underlineDomNode!}
        attachment={attachment}
        hoverLogic={hoverLogic}
      />
      <DotShadowContainer
        editorId={id}
        attachment={attachment}
        dotNode={dotDomNode!}
      />
    </>
  );
}

export default ContentEditableRoot;
